import { ParentOrganization } from '../../../data/models/parent-organization.model';
import { styled } from '@mui/material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { useCallback, useEffect } from 'react';
import { parentOrganizationsUtils } from '../../../services/parentOrganizationsUtils';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as parentOrganizationSlice from '../../../data/parent-organizations.slice';
import ParentOrganizationForm from './ParentOrganizationForm';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as uiSlice from '../../../data/ui.slice';

const PREFIX = 'ParentOrganizationDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));

interface Props {
  title: string;
  organization?: ParentOrganization;
  open: boolean;
  onClose: () => void;
}

function ParentOrganizationDialog({
  open,
  title,
  onClose,
  organization,
}: Props) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const formHook = useForm({
    mode: 'onTouched',
    defaultValues:
      parentOrganizationsUtils.getParentOrganizationDefaults(organization),
  });
  const savingOrganization = useAppSelector(
    parentOrganizationSlice.selectSavingOrganization,
  );
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);

  const handleClose = useCallback(() => {
    onClose();
  }, []);

  const handleSave = (data: ParentOrganization) => {
    const newOrganization =
      parentOrganizationsUtils.getOrganizationFromFormData(data, false);
    if (organization) {
      dispatch(
        parentOrganizationSlice.updateOrganizationAsync(newOrganization),
      );
    } else {
      dispatch(
        parentOrganizationSlice.createOrganizationAsync(newOrganization),
      );
    }
  };

  useEffect(() => {
    if (saveSuccessMessage) {
      handleClose();
    }
  }, [saveSuccessMessage]);

  useEffect(() => {
    const defaults =
      parentOrganizationsUtils.getParentOrganizationDefaults(organization);
    formHook.reset(defaults);
  }, [open]);

  return (
    <RootDialog open={open}>
      <DialogTitle>
        {title}
        {savingOrganization && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <ParentOrganizationForm isNew={!organization} formHook={formHook} />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !formHook.formState.isValid ||
            !formHook.formState.isDirty ||
            savingOrganization
          }
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.add')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}

export default ParentOrganizationDialog;
